/** The frame every screen sits in: rail, top bar, page. */
import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { del } from "../lib/api";
import { usePermission, useSession } from "../hooks/useSession";
import { applyTheme, nextTheme, readTheme, type Theme } from "../lib/theme";
import type { CollectionRef, JobList } from "../lib/types";
import { Rail, RailStat } from "./Rail";
import { Button } from "./ui";

const THEME_GLYPH: Record<Theme, string> = { dark: "dark", light: "light", system: "auto" };

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => applyTheme(theme), [theme]);
  return (
    <Button
      variant="ghost"
      onClick={() => setTheme(nextTheme(theme))}
      title="Dark, light, or follow the system"
    >
      {THEME_GLYPH[theme]}
    </Button>
  );
}

/**
 * `fill` turns the page into an application shell: the frame is fixed at viewport height
 * and the content pane is the only thing that can scroll, so panes inside it can claim
 * the remaining space and scroll independently. Below `lg` it reverts to ordinary
 * document flow — a phone has no room for panes and needs the page to scroll.
 */
export function Shell({
  crumb,
  children,
  fill = false,
}: {
  crumb: ReactNode;
  children: ReactNode;
  fill?: boolean;
}) {
  const { me } = useSession();
  const can = usePermission();
  const collections = useQuery({
    queryKey: ["collections", "rail"],
    queryFn: () => get<{ collections: CollectionRef[] }>("/v1/collections"),
  });

  // Queue depth is the one number worth having without navigating anywhere.
  const queued = useQuery({
    queryKey: ["jobs", "queued"],
    queryFn: () => get<JobList>("/v1/jobs?state=queued&limit=200"),
    refetchInterval: 10_000,
  });
  const running = useQuery({
    queryKey: ["jobs", "running"],
    queryFn: () => get<JobList>("/v1/jobs?state=running&limit=200"),
    refetchInterval: 10_000,
  });

  const busy = (running.data?.jobs?.length ?? 0) > 0;

  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-[228px_minmax(0,1fr)] ${
        fill ? "min-h-screen lg:h-screen lg:overflow-hidden" : "min-h-screen"
      }`}
    >
      <div className="hidden lg:block">
        <Rail
          footer={
            <>
              <RailStat label="Queued" value={queued.data?.jobs?.length ?? "—"} />
              <RailStat label="Running" value={running.data?.jobs?.length ?? "—"} />
              <div className="flex items-center gap-2 pt-1 font-mono text-[10px] tracking-[0.16em] uppercase">
                <i className={`dot ${busy ? "pulse bg-gold" : "bg-ok"}`} />
                <span className="text-faint">{busy ? "working" : "idle"}</span>
              </div>
            </>
          }
          can={can}
          collections={collections.data?.collections ?? []}
        />
      </div>

      {/* grid-cols-[minmax(0,1fr)] is load-bearing, not decoration: a grid with only a
          row template has one implicit column sized `auto`, which is max-content — so a
          single unbroken 120-character filename widens this column past the viewport and
          takes every pane on the page with it. min-w-0 on <main> does not help, because
          it is the track that overflows, not the element. */}
      <main
        className={`min-w-0 ${
          fill ? "lg:grid lg:h-screen lg:grid-cols-[minmax(0,1fr)] lg:grid-rows-[auto_minmax(0,1fr)]" : ""
        }`}
      >
        <header className="sticky top-0 z-20 flex h-14 items-center gap-4 border-b border-hair bg-ground/95 px-6 backdrop-blur-[14px]">
          <div className="flex min-w-0 items-center gap-2 font-mono text-[11.5px] tracking-[0.1em] text-faint uppercase">
            {crumb}
          </div>
          <div className="ms-auto flex items-center gap-3">
            <span className="flex items-center gap-2 border border-hair px-2.5 py-1 font-mono text-[10.5px] text-dim">
              <i className="dot bg-ok" />
              {me?.display_name ?? "key"}
              <b className="text-faint">[{me?.role ?? "—"}]</b>
            </span>
            <ThemeToggle />
            <Button
              variant="ghost"
              onClick={() => {
                void del("/v1/sessions").finally(() => window.location.assign("/login"));
              }}
              title="Sign out"
            >
              sign out
            </Button>
          </div>
        </header>

        <div
          className={
            fill
              ? "flex min-h-0 flex-col gap-4 px-6 pt-5 pb-5 lg:overflow-hidden"
              : "flex flex-col gap-5 px-6 pt-6 pb-14"
          }
        >
          {children}
        </div>
      </main>
    </div>
  );
}

/** Breadcrumb pieces, so every screen renders the trail the same way. */
export function Sep() {
  return <span className="text-hair-strong">/</span>;
}
